import { useEffect, useState } from "react"

/**
 * Per-device weight-unit preference. Storage is always kg (canonical, matches
 * backend column). This hook is a thin localStorage wrapper + storage-event
 * subscription so all mounted components stay in sync within and across tabs.
 *
 * Why localStorage and not a server column: this is a display preference per
 * device, not user identity. A user might prefer lb on their laptop and kg on
 * their phone. Avoiding the column also dodges a /me payload change + a
 * profiles migration.
 */
export type WeightUnit = "kg" | "lb"

const STORAGE_KEY = "fittrack:weight_unit"
// Exact conversion: 1 lb = 0.45359237 kg (international avoirdupois pound).
const KG_PER_LB = 0.45359237

function readStored(): WeightUnit {
  if (typeof window === "undefined") return "kg"
  const v = window.localStorage.getItem(STORAGE_KEY)
  return v === "lb" ? "lb" : "kg"
}

export function useWeightUnit() {
  const [unit, setUnitState] = useState<WeightUnit>(readStored)

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setUnitState(readStored())
    }
    const onLocal = () => setUnitState(readStored())
    window.addEventListener("storage", onStorage)
    window.addEventListener("fittrack:unit-changed", onLocal)
    return () => {
      window.removeEventListener("storage", onStorage)
      window.removeEventListener("fittrack:unit-changed", onLocal)
    }
  }, [])

  function setUnit(u: WeightUnit) {
    window.localStorage.setItem(STORAGE_KEY, u)
    // Same-tab notify: the `storage` event only fires on OTHER tabs.
    window.dispatchEvent(new Event("fittrack:unit-changed"))
    setUnitState(u)
  }

  return { unit, setUnit }
}

// Round to 1 decimal — matches DB NUMERIC(5,2) granularity and prevents the
// ugly float-display artifacts on every keystroke (e.g. 75.07 * 2.20462 →
// 165.50031964... → "165.5").
function round1(v: number): number {
  return Math.round(v * 10) / 10
}

/** Kilograms → user's display unit (number, rounded to 1 decimal). */
export function toDisplay(kg: number, unit: WeightUnit): number {
  if (!Number.isFinite(kg) || kg <= 0) return 0
  if (unit === "kg") return round1(kg)
  return round1(kg / KG_PER_LB)
}

/** User-typed display value → kilograms (full precision, no rounding). */
export function fromDisplay(value: number, unit: WeightUnit): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  if (unit === "kg") return value
  return value * KG_PER_LB
}

/** "75.2 kg" / "165.8 lb" — for read-only display. */
export function formatDisplay(kg: number, unit: WeightUnit, decimals = 1): string {
  if (!Number.isFinite(kg) || kg <= 0) return "—"
  const v = unit === "kg" ? kg : kg / KG_PER_LB
  return `${v.toFixed(decimals)} ${unit}`
}

/** Signed delta in user's display unit, with 2-decimal precision. */
export function formatDelta(deltaKg: number, unit: WeightUnit): string {
  const v = unit === "kg" ? deltaKg : deltaKg / KG_PER_LB
  const sign = v > 0 ? "+" : ""
  return `${sign}${v.toFixed(2)} ${unit}`
}
